import { ArrowLeft, Download } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { downloadCafCsv, getCafMonitor } from "@/features/eservices/api";
import type { CafMonitorRow } from "@/features/eservices/types";
import { cn } from "@/lib/utils";

export function CafMonitorPage() {
  const [rows, setRows] = useState<CafMonitorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState("sent");

  useEffect(() => {
    setLoading(true);
    getCafMonitor(ordering)
      .then(setRows)
      .catch(() => toast.error("Could not load the CAF report."))
      .finally(() => setLoading(false));
  }, [ordering]);

  const grouped = useMemo(() => {
    const byStatus: Record<string, CafMonitorRow[]> = {};
    for (const r of rows) (byStatus[r.status] ??= []).push(r);
    return byStatus;
  }, [rows]);

  const exportCsv = async () => {
    try {
      const blob = await downloadCafCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "caf-monitor.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/eservices"
            className="text-muted-foreground mb-1 flex items-center gap-1 text-sm hover:underline"
          >
            <ArrowLeft className="size-3" /> e-Services
          </Link>
          <h1 className="text-foreground text-2xl font-semibold">CAF Monitoring</h1>
        </div>
        <Button variant="outline" onClick={exportCsv}>
          <Download className="mr-1 size-4" /> Export CSV
        </Button>
      </div>

      {loading ? (
        <Loader />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">No CAF monitors being tracked.</p>
      ) : (
        Object.entries(grouped).map(([status, statusRows]) => (
          <Card key={status} className="gap-0 overflow-hidden p-0">
            <div className="bg-muted/40 border-b px-4 py-2 text-sm font-semibold">
              {status} ({statusRows.length})
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button onClick={() => setOrdering("taxpayer")}>Taxpayer</button>
                  </TableHead>
                  <TableHead>Form</TableHead>
                  <TableHead>Tax Years</TableHead>
                  <TableHead>
                    <button onClick={() => setOrdering("sent")}>Date Sent</button>
                  </TableHead>
                  <TableHead>Last Check</TableHead>
                  <TableHead>Weeks</TableHead>
                  <TableHead>Practitioner</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statusRows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.taxpayer}</TableCell>
                    <TableCell>{r.poaType}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {[r.yearFrom, r.yearTo].filter(Boolean).join("–") || "—"}
                    </TableCell>
                    <TableCell>{r.dateSent ?? "—"}</TableCell>
                    <TableCell>{r.lastCheck ?? "—"}</TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "font-medium",
                          r.weeks != null && r.weeks >= 11 && "text-destructive font-bold",
                        )}
                      >
                        {r.weeks ?? "—"}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {r.practitionerName || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ))
      )}
      <p className="text-muted-foreground text-xs">
        Monitors pending 11+ weeks are flagged; those over 12 weeks are dropped
        automatically by the nightly check. <Badge variant="outline">Mon–Thu</Badge>
      </p>
    </div>
  );
}
